import { Box, Button, makeStyles, Typography } from "@material-ui/core";
import {
  AdhocAgreementStatuses,
  AdhocProject
} from "integrity-owls-logic/dist";
import React, { useCallback, useEffect, useState } from "react";
import { BLUE, GRAY_DARKEST } from "../../../constants";
import adhocAgreementsService from "../../../services/AdhocAgreementsService";
import BasicSearchField from "../../shared/BasicSearchField";
import BasicSelect from "../../shared/BasicSelect";
import AdhocAgreementTablePagination from "./table/Pagination";
import { AdhocAgreementsTable } from "./table/Table";

export const AdhocAgreementsContent: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number | string>(10);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(
    AdhocAgreementStatuses.SENT_UNSIGNED
  );
  const [projects, setProjects] = useState<AdhocProject[]>([]);

  useEffect(() => {
    const getData = async () => {
      const response = await adhocAgreementsService.getAdhocAgreements({
        searchText,
        page,
        rowsPerPage: typeof rowsPerPage === "string" ? -1 : rowsPerPage,
        status: selectedStatus === "All" ? "" : selectedStatus,
        projectId: selectedProject === "All" ? "" : selectedProject
      });
      console.log("selectedProject", selectedProject);
      console.log("response: ", response);
      if (response) {
        setAgreements(response.data);
        setTotalRecords(response.total);
      }
      setLoading(false);
    };
    getData();
  }, [selectedStatus, page, rowsPerPage, selectedProject]);

  useEffect(() => {
    const fetchProjects = async () => {
      const response = await adhocAgreementsService.getAdhocProjects({
        page: 0,
        rowsPerPage: -1
      });
      setProjects(response.data);
    };
    fetchProjects();
  }, []);

  const handleStatusChange = useCallback((status: string) => {
    setPage(0);
    setSelectedStatus(status);
  }, []);

  const handleSelectProject = useCallback((projectId: string) => {
    setPage(0);
    setSelectedProject(projectId);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: string) => {
    setRowsPerPage(rowsPerPage);
  }, []);

  const handleSearch = useCallback(async () => {
    console.log("handleSearch");
    console.log("searchText: ", searchText);
    console.log("selectedProject: ", selectedProject);
    console.log("selectedStatus: ", selectedStatus);

    const getData = async () => {
      const response = await adhocAgreementsService.getAdhocAgreements({
        searchText,
        page,
        rowsPerPage: typeof rowsPerPage === "string" ? -1 : rowsPerPage,
        status: selectedStatus === "All" ? "" : selectedStatus,
        projectId: selectedProject === "All" ? "" : selectedProject
      });
      console.log("selectedProject", selectedProject);
      console.log("response: ", response);
      if (response) {
        setAgreements(response.data);
        setTotalRecords(response.total);
      }
      setLoading(false);
    };
    getData();
  }, [searchText, selectedProject, selectedStatus]);

  const changePage = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [page]
  );

  const renderStatusTabs = useCallback(() => {
    const classes = useStyles();
    console.log("selectedStatus: ", selectedStatus);
    return ["All", ...Object.values(AdhocAgreementStatuses)].map(status => {
      return (
        <Box key={status}>
          <Box
            className={`${classes.tabContainer} ${
              status === selectedStatus ? classes.activeTab : ""
            }`}
            onClick={() => handleStatusChange(status)}
          >
            <Typography noWrap>
              {status
                .split("_")
                .map(
                  word =>
                    word.charAt(0).toUpperCase() + word.toLowerCase().slice(1)
                )
                .join(" ")}
            </Typography>
          </Box>
        </Box>
      );
    });
  }, [selectedStatus]);

  return (
    <Box
      display="flex"
      style={{ width: "100%" }}
      p={2}
      pt={1}
      flexDirection="column"
    >
      <Box flexDirection="row" display="flex" width="100%">
        <Box flex={5}>
          <BasicSearchField
            label="Search by ID, licensee, or license name..."
            searchText={searchText}
            handleFieldChange={e => setSearchText(e.currentTarget.value)}
            handleKeyPress={e => e.key === "Enter" && handleSearch()}
          />
        </Box>
        <Box flex={1} ml={2}>
          <BasicSelect
            label="Project"
            options={[
              { id: "All", name: "All" },
              ...projects.map(project =>
                project.active
                  ? {
                      id: project.id,
                      name: project.name
                    }
                  : {
                      id: project.id,
                      name: `${project.name} (inactive)`
                    }
              )
            ]}
            value={selectedProject}
            handleChange={e => handleSelectProject(e)}
          />
        </Box>
        <Box
          flex={1}
          ml={2}
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSearch()}
          >
            Search
          </Button>
        </Box>
        <Box flex={4} display="flex" justifyContent="flex-end">
          <BasicSelect
            options={["5", "10", "25", "50", "All"]}
            value={rowsPerPage}
            handleChange={e => handleRowsPerPageChange(e)}
          />
        </Box>
      </Box>

      <Box flexDirection="column" display="flex" width="100%">
        <Box my={2} display="flex" flexDirection="row">
          {renderStatusTabs()}
        </Box>
        <AdhocAgreementsTable agreements={agreements} loading={loading} />
      </Box>

      <Box flexDirection="row" display="flex" width="100%">
        <AdhocAgreementTablePagination
          page={page}
          changePage={changePage}
          rowsPerPage={
            rowsPerPage === "All" ? totalRecords : (rowsPerPage as number)
          }
          totalRecords={totalRecords}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  activeTab: {
    backgroundColor: GRAY_DARKEST,
    borderBottom: "2px solid " + BLUE,
    fontWeight: "bold"
  },
  tabContainer: {
    "&:hover": {
      cursor: "pointer"
    },
    borderTopLeftRadius: "5px",
    borderTopRightRadius: "5px",
    boxSizing: "border-box",
    padding: "20px 40px",
    margin: "0 5px"
  }
}));

export default AdhocAgreementsContent;
